// User-defined kernel definitions. A kernel is a deterministic
// recipe: a list of analytics_query + python steps producing a
// final value, plus a render spec describing how to display it.
// Saved in localStorage; addressable as a slash command.
//
// Shape:
//   {
//     id: string,                // stable identifier (uuid-ish)
//     command: string,           // slash-command name (kebab-case)
//     title: string,             // human label
//     description?: string,
//     scopeMode?: "scoped"|"global",  // "scoped": the activity surface
//                                     // re-runs this kernel narrowed to
//                                     // the active scope (prompt-rooted
//                                     // queries only; see kernelScope).
//                                     // "global" (default): runs tenant-
//                                     // wide everywhere.
//     agentic?: true,            // present only when the kernel contains an
//                                // `agent` step. Marks the kernel as NON-
//                                // deterministic; gated at save (agent steps
//                                // are rejected without it).
//     params: {                  // optional parameter declarations
//       <name>: { type: "number"|"string"|"boolean", default?: any }
//     },
//     steps: [                   // ordered execution
//       { kind: "analytics_query", id, spec },        // raw Query spec
//       { kind: "python", id, code, inputs?: { name: stepId },
//         packages?: ("numpy"|"pandas"|"scipy")[] },
//       { kind: "mcp_tool", id, server, tool, args?, argsFrom? },
//       { kind: "agent", id, prompt, inputs?, outputSchema },  // agentic only
//     ],
//     render: { kind, ... },     // KernelBlock spec
//     renderSource?: stepId,     // which step's output → render's `data`
//     createdAt, updatedAt,
//   }
//
// Parameter substitution happens at execution time: any `{{param}}`
// or `{{param.field}}` placeholder in analytics_query spec strings
// or python code gets replaced before the step runs. See kernelRunner.

import { KNOWN_RENDER_KINDS as PRIMITIVE_KINDS } from "./kernelPrimitiveSpecs.js";
import { normalizePythonPackageRequirements } from "./pythonPackageRequirements.js";
import { getCurrentTenantIdSync } from "./userInfo.js";
import { browserStorageKey, discardLegacyTenantEntry } from "./browserStorageScope.js";
import { BUILTIN_KERNELS, BUILTIN_KERNEL_COMMANDS } from "./builtins/builtinKernels.js";
import { createKernelResolver } from "./kernelResolver.js";
import {
    executeCloudTenantViewCreate,
    getTenantViewCloudState,
    listCloudKernelCatalogRows,
    listCloudKernelRows,
    loadTenantViewBody,
    loadTenantViewCloud,
    markTenantViewCloudUnavailable,
    prepareCloudTenantViewCreate,
    subscribeTenantViewCloud,
    tenantViewDefinitionDiffers,
    updateCloudTenantView,
} from "./tenantViewCloudStore.js";
import { TenantViewUnavailableError } from "./tenantViewsApi.js";
import { openTenantViewWriteJournal } from "./tenantViewWriteJournal.js";
import {
    TenantViewLockUnavailableError,
    TenantViewOperationInProgressError,
    withTenantViewBrowserLock,
} from "./tenantViewBrowserLock.js";

const STORAGE_KEY_PREFIX = "originChat.kernels.v2";
const LEGACY_STORAGE_KEY_PREFIX = "originChat.kernels.v1";

// Render kinds the dashboard's KernelBlock dispatcher actually
// supports. Re-exported from the primitive spec module so the
// validators stay aligned with the renderer (single source of truth).
export const KNOWN_RENDER_KINDS = PRIMITIVE_KINDS;

// Resolve the bucket for a tenant. Callers historically passed `null`
// (no tenant threaded through), which landed everything in a shared
// `:default` bucket — not actually scoped. We now fall back to the
// signed-in tenant from /v1/me so kernels are isolated per tenant; the
// `:default` bucket is used only before /v1/me resolves, and its
// pre-scoping contents are preserved for cloud import.
function storageKey(tenantId) {
    discardLegacyTenantEntry(globalThis.localStorage, LEGACY_STORAGE_KEY_PREFIX, tenantId);
    return browserStorageKey(STORAGE_KEY_PREFIX, tenantId);
}

function resolvedBucketId(tenantId, currentTenantId = getCurrentTenantIdSync()) {
    return String((tenantId ?? currentTenantId) || "default");
}

function effectiveCloudTenantId(tenantId, cloudTenantId, currentTenantId) {
    const explicit = cloudTenantId === undefined ? null : cloudTenantId;
    const localTenant = tenantId && tenantId !== "default" ? tenantId : null;
    const value = explicit
        ?? localTenant
        ?? currentTenantId
        ?? getTenantViewCloudState(null).tenantId;
    return value ? String(value) : null;
}

function deferredCloudSave(staged, status, message) {
    return {
        ...staged,
        cloudSaveStatus: status,
        cloudSaveMessage: message,
    };
}

function committedWithLocalDurabilityWarning(saved) {
    return {
        ...saved,
        localDurabilityWarning:
            "The kernel was created in cloud storage, but its local recovery marker could not be saved. Retry to finish local cleanup.",
    };
}

function latestStagedKernel(bucketId, staged) {
    return readRawUserKernels(bucketId).find((candidate) => candidate.id === staged.id)
        || staged;
}

function shouldRetireCreateRequest(error, { wasPending }) {
    if (error?.code === "INVALID_RESPONSE") return false;
    if (error?.code === "CLIENT_VALIDATION") return !wasPending;
    if (
        error?.serverMessage === "the idempotent create result was deleted"
        || error?.code === "ALREADY_EXISTS"
        || [400, 409, 413, 422].includes(Number(error?.status))
    ) {
        return true;
    }
    return false;
}

// Lossless migration read. Unlike listKernels/readUserKernels, this does not
// merge bundled kernels, filter colliding commands, or discard malformed rows.
// The bucket id is explicit so a caller asking for the legacy `default` bucket
// cannot be silently redirected to the active tenant after /v1/me resolves.
//
// Absence, invalid JSON/shape, and unavailable storage are distinct outcomes:
// migration may treat absence as complete, but must preserve and retry either
// failure rather than interpreting it as an empty library.
export function readRawKernelBucket(bucketId = "default") {
    const id = bucketId == null || bucketId === "" ? "default" : String(bucketId);
    const key = storageKey(id);
    if (!key) return { status: "absent", key: null, records: [] };
    let raw;
    try {
        raw = localStorage.getItem(key);
    } catch (error) {
        return { status: "unavailable", reason: "storage", key, records: [], error };
    }
    if (raw === null) return { status: "absent", key, records: [] };

    let parsed;
    try {
        parsed = JSON.parse(raw);
    } catch (error) {
        return { status: "invalid", reason: "parse", key, records: [], error };
    }
    if (!Array.isArray(parsed)) {
        return {
            status: "invalid",
            reason: "shape",
            key,
            records: [],
            error: new TypeError("kernel bucket must contain a JSON array"),
        };
    }
    return { status: "ok", key, records: parsed };
}

const listeners = new Set();
function notify() {
    for (const fn of listeners) {
        try { fn(); } catch { /* listener errors don't block siblings */ }
    }
}

export function subscribeKernels(fn) {
    listeners.add(fn);
    const unsubscribeCloud = subscribeTenantViewCloud(fn);
    return () => {
        listeners.delete(fn);
        unsubscribeCloud();
    };
}

// List kernels for a tenant: the read-only built-in set (shipped in code,
// never persisted) merged with the tenant's own saved kernels. Built-ins are
// always present even with empty storage; a user command that collides with a
// built-in command can't exist (saveKernel rejects it), but we filter defensively
// so the immutable built-in always wins. Sorted by command, matching prior shape.
export function listKernels(tenantId) {
    return listKernelCatalog(tenantId).map(({ body }) => body);
}

// Is this command one of the shipped, read-only built-in kernels?
export function isBuiltinKernel(command) {
    return BUILTIN_KERNEL_COMMANDS.has(sanitizeCommand(command));
}

// Raw persisted (user-owned) kernels only — the localStorage set, with no
// built-ins merged in. The write paths (saveKernel / deleteKernel) rebuild
// storage from THIS, never from listKernels, so the in-code built-ins are never
// accidentally baked into a tenant's localStorage.
function readUserKernels(tenantId) {
    try {
        const key = storageKey(tenantId);
        if (!key) return [];
        const raw = localStorage.getItem(key);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter(
            (k) => k && typeof k.id === "string" && typeof k.command === "string"
                && !BUILTIN_KERNEL_COMMANDS.has(k.command),
        );
    } catch {
        return [];
    }
}

export function kernelResolverForTenant(tenantId) {
    const cloud = listCloudKernelRows(tenantId);
    return createKernelResolver({
        bundledKernels: BUILTIN_KERNELS,
        viewerOwnedKernels: cloud.viewerOwned,
        visibleTenantKernels: cloud.tenantVisible,
        // These records predate source envelopes. Keep their viewer-owned
        // command rank, but fail closed on provenance until cloud reads put
        // them in the server-backed viewerOwnedKernels bucket.
        legacyViewerKernels: readUserKernels(tenantId),
    });
}

function kernelCatalogResolverForTenant(tenantId) {
    const cloud = listCloudKernelCatalogRows(tenantId);
    return createKernelResolver({
        bundledKernels: BUILTIN_KERNELS,
        viewerOwnedKernels: cloud.viewerOwned,
        visibleTenantKernels: cloud.tenantVisible,
        legacyViewerKernels: readUserKernels(tenantId),
    });
}

/**
 * Metadata-only catalog used for discovery and selection. Entries from this
 * function are never passed to the runner; resolveKernelBy*Async loads and
 * re-resolves the winning stored body first.
 */
export function listKernelCatalog(tenantId) {
    return kernelCatalogResolverForTenant(tenantId)
        .listByCommand()
        .sort((a, b) => a.body.command.localeCompare(b.body.command));
}

/**
 * Metadata-only catalog that intentionally retains lower-ranked command
 * matches. Discovery surfaces use it to make a viewer-owned definition
 * shadowing a tenant-published definition visible and author-disambiguated.
 */
export function listKernelCatalogCandidates(tenantId) {
    const resolver = kernelCatalogResolverForTenant(tenantId);
    const winners = new Set(resolver.listByCommand());
    return resolver.listCandidates()
        .map((candidate) => Object.freeze({
            ...candidate,
            isWinner: winners.has(candidate.resolvedDefinition),
        }))
        .sort((a, b) =>
            a.command.localeCompare(b.command)
            || Number(b.isWinner) - Number(a.isWinner)
        );
}

// Source-aware entry point for every consumer that resolves executable bytes.
// App slash commands and saved prompts deliberately stay outside this list and
// keep their existing higher rank in ChatConsole.
export function listResolvedKernels(tenantId) {
    return kernelResolverForTenant(tenantId)
        .listByCommand()
        .sort((a, b) => a.body.command.localeCompare(b.body.command));
}

export function resolveKernelByCommand(tenantId, command) {
    const target = sanitizeCommand(command);
    return target ? kernelResolverForTenant(tenantId).resolveByCommand(target) : null;
}

export function resolveKernelById(tenantId, id) {
    return id ? kernelResolverForTenant(tenantId).resolveById(id) : null;
}

async function loadCatalogWinner(tenantId, winner) {
    const body = winner?.body;
    if (body?.tenantViewId && body.bodyLoaded !== true) {
        await loadTenantViewBody(body.tenantViewId, { tenantId });
    }
    return body;
}

export async function resolveKernelByCommandAsync(tenantId, command) {
    const target = sanitizeCommand(command);
    if (!target) return null;
    const winner = kernelCatalogResolverForTenant(tenantId).resolveByCommand(target);
    if (!winner) return null;
    await loadCatalogWinner(tenantId, winner);
    return kernelResolverForTenant(tenantId).resolveByCommand(target);
}

export async function resolveKernelByIdAsync(tenantId, id) {
    if (!id) return null;
    const winner = kernelCatalogResolverForTenant(tenantId).resolveById(id);
    if (!winner) return null;
    await loadCatalogWinner(tenantId, winner);
    return kernelResolverForTenant(tenantId).resolveById(id);
}

// Truly raw persisted kernels — valid shape only, NO built-in filtering. Used by
// the collision migration, which needs to *see* a user row whose command shadows
// a built-in (the one thing readUserKernels drops) so it can rename rather than
// lose it.
function readRawUserKernels(tenantId) {
    try {
        const key = storageKey(tenantId);
        if (!key) return [];
        const raw = localStorage.getItem(key);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter(
            (k) => k && typeof k.id === "string" && typeof k.command === "string",
        );
    } catch {
        return [];
    }
}

// Derive a name from `root` that isn't taken (per `isTaken`), appending -2/-3/…
// on collision. Truncates the ROOT (not the appended suffix) to keep the result
// ≤ 64 chars, so the loop always terminates: slicing AFTER appending could chop
// `-2` back to `root` and spin forever once `root` is already ~64 chars.
function uniqueName(root, isTaken) {
    let candidate = root.slice(0, 64);
    let n = 1;
    while (isTaken(candidate)) {
        n += 1;
        const tag = `-${n}`;
        candidate = `${root.slice(0, 64 - tag.length)}${tag}`;
    }
    return candidate;
}

// One-time, idempotent upgrade migration. A kernel a tenant saved BEFORE the
// built-in set shipped can carry a `command` that now collides with a built-in
// (the starter-prompt-derived names the chat agent auto-mints — `token-spend`,
// `provider-mix`, … — are exactly the ones built-ins claim). readUserKernels
// filters such a row out, and because every write path rebuilds storage from that
// filtered reader, the next saveKernel/deleteKernel would purge it irreversibly.
// Rather than drop it, rename the colliding row to a free `<command>-saved` so the
// user keeps their kernel (now editable/forkable like any other). Persists only
// when it actually renames; safe to run on every boot.
export function migrateCollidingUserKernels(tenantId) {
    const rows = readRawUserKernels(tenantId);
    if (!rows.length) return false;
    const used = new Set(rows.map((k) => k.command));
    let changed = false;
    for (const k of rows) {
        if (!BUILTIN_KERNEL_COMMANDS.has(k.command)) continue;
        const next = uniqueName(`${k.command}-saved`, (c) => used.has(c) || BUILTIN_KERNEL_COMMANDS.has(c));
        used.delete(k.command);
        used.add(next);
        k.command = next;
        k.updatedAt = Date.now();
        changed = true;
    }
    if (!changed) return false;
    rows.sort((a, b) => a.command.localeCompare(b.command));
    const key = storageKey(tenantId);
    if (!key) return false;
    localStorage.setItem(key, JSON.stringify(rows));
    notify();
    return true;
}

export function findKernelByCommand(tenantId, command) {
    return resolveKernelByCommand(tenantId, command)?.body || null;
}

export function findKernelById(tenantId, id) {
    return resolveKernelById(tenantId, id)?.body || null;
}

export function findKernelInCatalogByCommand(tenantId, command) {
    const target = sanitizeCommand(command);
    return target
        ? kernelCatalogResolverForTenant(tenantId).resolveByCommand(target)?.body || null
        : null;
}

export function findKernelInCatalogById(tenantId, id) {
    return id
        ? kernelCatalogResolverForTenant(tenantId).resolveById(id)?.body || null
        : null;
}

// Validate the command a save targets. Split out of saveKernel so the cloud
// update path applies exactly the same rules without touching localStorage.
function kernelCommandForSave(kernel) {
    if (!kernel || typeof kernel !== "object") {
        throw new Error("invalid kernel");
    }
    const command = sanitizeCommand(kernel.command);
    if (!command) throw new Error("kernel needs a command name");
    // Built-in kernels are read-only and ship in code — never persist over one.
    // Fork it (forkKernel) to get a mutable copy under a new command instead.
    if (BUILTIN_KERNEL_COMMANDS.has(command)) {
        throw new Error(`"/${command}" is a built-in kernel (read-only). Fork it to customize.`);
    }
    return command;
}

// Validate a kernel and normalize it into the record shape every storage path
// persists. `prior` is the record this save replaces: the localStorage row on
// the staging path, the loaded cloud body on the update path. It supplies the
// identity fields (id, createdAt) a save must carry forward rather than mint
// anew.
function normalizedKernelEntry(kernel, command, prior = null) {
    if (!Array.isArray(kernel.steps) || !kernel.steps.length) {
        throw new Error("kernel needs at least one step");
    }
    const normalizedSteps = kernel.steps.map((step) => {
        if (step?.kind !== "python") return step;
        let packages;
        try {
            packages = normalizePythonPackageRequirements(step.packages);
        } catch {
            throw new Error(
                `python step ${JSON.stringify(step.id ?? null)} has invalid package requirements`,
            );
        }
        const { packages: _ignoredPackages, ...rest } = step;
        return {
            ...rest,
            ...(packages.length > 0 ? { packages } : {}),
        };
    });
    if (!kernel.render || typeof kernel.render !== "object") {
        throw new Error("kernel needs a render spec");
    }
    if (!KNOWN_RENDER_KINDS.has(kernel.render.kind)) {
        throw new Error(
            `kernel render is missing a valid \`kind\` (got ${JSON.stringify(kernel.render.kind)}). ` +
            `Must be one of: ${[...KNOWN_RENDER_KINDS].join(", ")}.`,
        );
    }
    // `agent` steps are the one non-deterministic step kind; they're only
    // allowed in a kernel explicitly flagged `agentic`. This keeps
    // deterministic and agentic kernels genuinely separate categories,
    // enforced at the store (not just by the extractor's prompt gating).
    if (kernel.steps.some((s) => s && s.kind === "agent") && kernel.agentic !== true) {
        throw new Error("agent steps require an agentic kernel (set `agentic: true`)");
    }
    const scopeMode = kernel.scopeMode == null ? "global" : kernel.scopeMode;
    if (scopeMode !== "global" && scopeMode !== "scoped") {
        throw new Error(`kernel scopeMode must be "global" or "scoped" (got ${JSON.stringify(scopeMode)})`);
    }
    return {
        id: kernel.id || prior?.id || newKernelId(),
        command,
        title: (kernel.title || "").trim() || command,
        description: (kernel.description || "").trim() || undefined,
        scopeMode,
        ...(kernel.agentic === true ? { agentic: true } : {}),
        params: kernel.params && typeof kernel.params === "object" ? kernel.params : {},
        steps: normalizedSteps,
        render: kernel.render,
        renderSource: kernel.renderSource || null,
        createdAt: kernel.createdAt || prior?.createdAt || Date.now(),
        updatedAt: Date.now(),
    };
}

export function saveKernel(tenantId, kernel) {
    const persistenceKey = storageKey(tenantId);
    if (!persistenceKey) throw new Error("verified tenant and user identity are required");
    const command = kernelCommandForSave(kernel);
    const existing = readUserKernels(tenantId);
    const prior = existing.find(
        (candidate) => candidate.id === kernel.id || candidate.command === command,
    );
    const entry = normalizedKernelEntry(kernel, command, prior);
    const list = existing.filter((k) => k.command !== command && k.id !== entry.id);
    list.push(entry);
    list.sort((a, b) => a.command.localeCompare(b.command));
    localStorage.setItem(persistenceKey, JSON.stringify(list));
    notify();
    return entry;
}

// The viewer-owned cloud kernel a save of `command` would land on, if any.
//
// A kernel's name IS its identity in cloud storage: tenant_views carries
// UNIQUE (tenant_id, created_by, kind, name) for tenant-owned rows, so a second
// CreateTenantView under a command the viewer already owns can only ever come
// back 409 "name is already in use". Saving an existing command is therefore an
// update of that row, never a create.
//
// Matched by command alone, deliberately. Fork and blank-create clone a source
// body (tenantViewId and all) and only swap the command, so trusting a carried
// tenantViewId here would silently overwrite the fork's source.
function viewerOwnedCloudKernelRow(cloudTenantId, command) {
    return listCloudKernelCatalogRows(cloudTenantId)
        .viewerOwned
        .find((row) => row.body.command === command) || null;
}

// Does an unfinished create still own this kernel?
//
// A create whose response was lost, or whose completion marker could not be
// written, leaves a durable journal entry bound to one request id. Replaying
// that exact request is what makes the create idempotent and what retires both
// the entry and the staging row. Routing such a retry to an update instead
// would strand the entry forever, so the create machinery keeps priority and
// the update path only applies once nothing is outstanding.
function hasOutstandingCreateJournal(cloudTenantId, bucketId, kernel, command) {
    const priorLocal = readUserKernels(bucketId).find(
        (candidate) => candidate.id === kernel.id || candidate.command === command,
    );
    const sourceKey = kernel.id || priorLocal?.id;
    if (!sourceKey) return false;
    try {
        return openTenantViewWriteJournal({
            tenantId: cloudTenantId,
            operation: "create",
            kind: "kernel",
            sourceKey,
        }).read() != null;
    } catch {
        // Malformed durable state fails closed through the create path, which
        // knows how to park it rather than write past it.
        return true;
    }
}

// Update the existing cloud row in place. Mirrors what Studio's editor already
// does (KernelsPage.persistDraft) so every authoring surface (the chat save
// agent, the canvas fan-out, KernelEditor's own fallback) agrees on what
// re-saving a command means. No staging row: the caller keeps the rejected
// definition in hand (kernelSaveAgent returns it as `kernel`), and a local row
// shadowing a live cloud row would outrank it in the resolver.
async function updateExistingCloudKernel(
    cloudTenantId,
    bucketId,
    row,
    kernel,
    command,
    { lockManager },
) {
    const tenantViewId = row.metadata.id;
    const lockIdentity = [
        encodeURIComponent(cloudTenantId),
        "kernel",
        encodeURIComponent(tenantViewId),
    ].join(":");
    try {
        // The merge in updateCloudTenantView diffs against the stored
        // definition, so a metadata-only catalog row has to be filled in first
        // or the edit would be merged onto an empty base.
        const base = row.body.bodyLoaded === true
            ? row.body
            : await loadTenantViewBody(tenantViewId, { tenantId: cloudTenantId });
        const entry = normalizedKernelEntry(kernel, command, base);
        const synchronized = await withTenantViewBrowserLock(
            lockIdentity,
            () => updateCloudTenantView(base, entry),
            { lockManager },
        );
        // A caller replaying an older staging row (the pre-cloud recovery path)
        // hands one back; exact-content match leaves a genuinely newer local
        // revision alone.
        deleteStagedKernelRevision(bucketId, kernel);
        return synchronized;
    } catch (error) {
        if (error instanceof TenantViewOperationInProgressError) {
            return deferredCloudSave(kernel, "in_progress", error.message);
        }
        if (error instanceof TenantViewLockUnavailableError) {
            return deferredCloudSave(kernel, "cloud_unavailable", error.message);
        }
        if (error instanceof TenantViewUnavailableError) {
            markTenantViewCloudUnavailable(cloudTenantId, error);
            return deferredCloudSave(kernel, "cloud_unavailable", error.message);
        }
        throw error;
    }
}

// A create that came back ALREADY_EXISTS proves the catalog this tab is holding
// is stale: another tab or device minted the command after this tab went ready,
// and the pre-flight lookup only refreshes an idle or loading catalog. Without
// this, every retry repeats the create and 409s again until something else
// forces a reload. Refresh once and route to the row that now exists; return
// null when it still is not visible (someone else's row, or a delete that
// raced us) so the caller rethrows the original error.
async function recoverFromKernelNameConflict(
    cloudTenantId,
    bucketId,
    kernel,
    command,
    { lockManager },
) {
    const refreshed = await loadTenantViewCloud(cloudTenantId, { force: true });
    if (refreshed.status !== "ready") return null;
    const row = viewerOwnedCloudKernelRow(cloudTenantId, command);
    if (!row) return null;
    return updateExistingCloudKernel(
        cloudTenantId,
        bucketId,
        row,
        kernel,
        command,
        { lockManager },
    );
}

/**
 * Production authoring path. A local row is used as a recoverable staging
 * record only while the cloud catalog/API is unavailable; successful cloud
 * creates remove that staging row immediately.
 */
export async function saveKernelCloudFirst(
    tenantId,
    kernel,
    { cloudTenantId, lockManager = globalThis.navigator?.locks } = {},
) {
    const currentTenantId = getCurrentTenantIdSync();
    const targetTenantId = effectiveCloudTenantId(
        tenantId,
        cloudTenantId,
        currentTenantId,
    );
    const bucketId = resolvedBucketId(tenantId, currentTenantId);
    if (targetTenantId) {
        let state = getTenantViewCloudState(targetTenantId);
        if (state.status === "idle" || state.status === "loading") {
            state = await loadTenantViewCloud(targetTenantId);
        }
        // Re-saving a command the viewer already owns is an update, not a
        // second create. Resolved before staging so the update path never
        // leaves a local row shadowing the cloud row it just wrote.
        const command = kernelCommandForSave(kernel);
        const existingRow = hasOutstandingCreateJournal(targetTenantId, bucketId, kernel, command)
            ? null
            : viewerOwnedCloudKernelRow(targetTenantId, command);
        if (existingRow) {
            return updateExistingCloudKernel(
                targetTenantId,
                bucketId,
                existingRow,
                kernel,
                command,
                { lockManager },
            );
        }
    }
    const staged = saveKernel(bucketId, kernel);
    if (!targetTenantId) {
        return deferredCloudSave(
            staged,
            "cloud_unavailable",
            "The active tenant was not resolved; this kernel remains stored locally.",
        );
    }
    const lockIdentity = [
        encodeURIComponent(targetTenantId),
        "kernel",
        encodeURIComponent(staged.id),
    ].join(":");
    try {
        return await withTenantViewBrowserLock(
            lockIdentity,
            () => saveStagedKernelCloudFirst(bucketId, targetTenantId, staged, { lockManager }),
            { lockManager },
        );
    } catch (error) {
        if (error instanceof TenantViewOperationInProgressError) {
            return deferredCloudSave(
                latestStagedKernel(bucketId, staged),
                "in_progress",
                error.message,
            );
        }
        if (error instanceof TenantViewLockUnavailableError) {
            return deferredCloudSave(
                staged,
                "cloud_unavailable",
                error.message,
            );
        }
        throw error;
    }
}

async function saveStagedKernelCloudFirst(bucketId, cloudTenantId, staged, { lockManager } = {}) {
    let state = getTenantViewCloudState(cloudTenantId);
    if (state.status === "idle" || state.status === "loading") {
        state = await loadTenantViewCloud(cloudTenantId);
    }
    const canRetryPendingCreate =
        state.status === "unavailable" && state.capabilities != null;
    if (state.status !== "ready" && !canRetryPendingCreate) {
        return deferredCloudSave(
            staged,
            "cloud_unavailable",
            state.error?.message || "Cloud view storage is unavailable; this kernel remains stored locally.",
        );
    }
    const journal = openTenantViewWriteJournal({
        tenantId: cloudTenantId,
        operation: "create",
        kind: "kernel",
        sourceKey: staged.id,
    });
    const durable = journal.read();
    if (durable?.state === "complete") {
        const refreshed = await loadTenantViewCloud(state.tenantId, { force: true });
        if (refreshed.status !== "ready") {
            return deferredCloudSave(
                staged,
                "cloud_unavailable",
                refreshed.error?.message || "Cloud view storage is unavailable; this kernel remains stored locally.",
            );
        }
        const row = refreshed.rows.find(
            (candidate) => candidate.metadata.id === durable.tenantViewId,
        );
        if (!row) {
            return deferredCloudSave(
                staged,
                "cloud_unavailable",
                "The previously created kernel is not in the readable catalog.",
            );
        }
        const resumed = await loadTenantViewBody(durable.tenantViewId, {
            tenantId: cloudTenantId,
        });
        let synchronized = resumed;
        if (tenantViewDefinitionDiffers(resumed, staged)) {
            synchronized = await updateCloudTenantView(resumed, staged);
            try {
                journal.complete(
                    durable,
                    synchronized.tenantViewId,
                    synchronized.version,
                );
            } catch {
                return committedWithLocalDurabilityWarning(synchronized);
            }
        }
        if (!deleteStagedKernelRevision(bucketId, staged)) {
            return deferredCloudSave(
                latestStagedKernel(bucketId, staged),
                "newer_revision",
                "A newer local kernel revision is waiting to be saved.",
            );
        }
        journal.clear();
        return synchronized;
    }
    if (durable?.state === "pending" && !durable.request) {
        return deferredCloudSave(
            staged,
            "legacy_journal_parked",
            "This interrupted kernel save predates durable request binding and was parked safely.",
        );
    }
    const wasPending = durable?.state === "pending";
    const pending = durable || journal.begin(
        prepareCloudTenantViewCreate("kernel", staged, {
            capabilities: state.capabilities,
        }),
    );

    let saved;
    try {
        saved = await executeCloudTenantViewCreate(pending.request, {
            capabilities: state.capabilities,
            createRequestId: pending.createRequestId,
        });
    } catch (error) {
        if (error instanceof TenantViewUnavailableError) {
            markTenantViewCloudUnavailable(state.tenantId, error);
            return deferredCloudSave(
                staged,
                "cloud_unavailable",
                error.message,
            );
        }
        if (shouldRetireCreateRequest(error, { wasPending })) journal.clear();
        if (error?.code === "ALREADY_EXISTS") {
            const recovered = await recoverFromKernelNameConflict(
                cloudTenantId,
                bucketId,
                staged,
                staged.command,
                { lockManager },
            );
            if (recovered) return recovered;
        }
        throw error;
    }
    try {
        journal.complete(pending, saved.tenantViewId, saved.version);
    } catch {
        return committedWithLocalDurabilityWarning(saved);
    }
    let synchronized = saved;
    const currentRequest = prepareCloudTenantViewCreate("kernel", staged, {
        capabilities: state.capabilities,
    });
    if (
        wasPending
        && JSON.stringify(currentRequest) !== JSON.stringify(pending.request)
    ) {
        synchronized = await updateCloudTenantView(saved, staged);
        try {
            journal.complete(
                pending,
                synchronized.tenantViewId,
                synchronized.version,
            );
        } catch {
            return committedWithLocalDurabilityWarning(synchronized);
        }
    }
    if (!deleteStagedKernelRevision(bucketId, staged)) {
        return deferredCloudSave(
            latestStagedKernel(bucketId, staged),
            "newer_revision",
            "A newer local kernel revision is waiting to be saved.",
        );
    }
    journal.clear();
    return synchronized;
}

function deleteStagedKernelRevision(bucketId, staged) {
    const existing = readRawUserKernels(bucketId);
    const next = existing.filter((candidate) => {
        if (candidate.id !== staged.id) return true;
        // A duplicate Save while the create is in flight rewrites only the
        // local timestamp before the browser lock reports `in_progress`. That
        // is the same durable revision, not newer work that must be retained.
        const { updatedAt: _candidateUpdatedAt, ...candidateRevision } = candidate;
        const { updatedAt: _stagedUpdatedAt, ...stagedRevision } = staged;
        return JSON.stringify(candidateRevision) !== JSON.stringify(stagedRevision);
    });
    if (next.length === existing.length) return false;
    const key = storageKey(bucketId);
    if (!key) return false;
    localStorage.setItem(key, JSON.stringify(next));
    notify();
    return true;
}

export function deleteKernel(tenantId, command) {
    const cmd = sanitizeCommand(command);
    if (!cmd) return false;
    // Built-ins ship in code and have no storage row to remove.
    if (BUILTIN_KERNEL_COMMANDS.has(cmd)) return false;
    const list = readUserKernels(tenantId);
    const next = list.filter((k) => k.command !== cmd);
    if (next.length === list.length) return false;
    const key = storageKey(tenantId);
    if (!key) return false;
    localStorage.setItem(key, JSON.stringify(next));
    notify();
    return true;
}

// Fork a kernel (built-in or user) into a fresh, mutable user-owned copy under a
// unique command. The way to "customize" a read-only built-in: the original is
// untouched; the copy is a normal saved kernel the user can edit and delete.
// Returns the persisted copy, or null if the source command isn't found.
export function forkKernel(tenantId, command) {
    const source = findKernelByCommand(tenantId, command);
    if (!source) return null;
    const used = new Set(listKernels(tenantId).map((k) => k.command));
    const next = uniqueName(`${sanitizeCommand(command)}-copy`, (c) => used.has(c));
    // Deep-clone so the fork shares no structure with the (frozen) built-in.
    const clone = JSON.parse(JSON.stringify(source));
    delete clone.builtin;
    delete clone.id;
    clone.command = next;
    clone.title = source.title ? `${source.title} (copy)` : next;
    clone.createdAt = Date.now();
    return saveKernel(tenantId, clone);
}

// The no-op python step createBlankKernel seeds. Exported so the editor can
// recognise a placeholder it is safe to move the render binding away from —
// see KernelEditor's addStep. Changing the seed here without updating that
// check silently reinstates the "added a step, nothing rendered" trap.
export const BLANK_KERNEL_STEP_CODE = "result = 0\n";

export function isUntouchedBlankDraft(draft) {
    const steps = draft?.steps || [];
    if (steps.length !== 1) return false;
    const [only] = steps;
    return only.kind === "python"
        && only.id === draft.renderSource
        && only.code === BLANK_KERNEL_STEP_CODE
        && Object.keys(only.inputs || {}).length === 0;
}

// Create a new blank kernel with a unique command name. Used by the
// Library sidebar's "+ new" affordance — gives the user a valid
// starting point (one no-op python step, a stat render bound to it)
// so the editor's preview / save flow works immediately instead of
// requiring the user to assemble the minimum schema by hand.
export function createBlankKernel(tenantId) {
    const existing = listKernels(tenantId);
    const used = new Set(existing.map((k) => k.command));
    let n = 1;
    let command = "new-kernel-1";
    while (used.has(command)) {
        n += 1;
        command = `new-kernel-${n}`;
    }
    const stepId = "s1";
    return saveKernel(tenantId, {
        command,
        title: "Untitled kernel",
        params: {},
        steps: [
            { kind: "python", id: stepId, code: BLANK_KERNEL_STEP_CODE, inputs: {} },
        ],
        render: { kind: "stat", label: "Untitled", format: "int" },
        renderSource: stepId,
    });
}

export async function forkKernelCloudFirst(tenantId, command) {
    const source = findKernelByCommand(tenantId, command);
    if (!source) return null;
    const used = new Set(listKernels(tenantId).map((k) => k.command));
    const next = uniqueName(`${sanitizeCommand(command)}-copy`, (c) => used.has(c));
    const clone = JSON.parse(JSON.stringify(source));
    delete clone.builtin;
    delete clone.id;
    clone.command = next;
    clone.title = source.title ? `${source.title} (copy)` : next;
    clone.createdAt = Date.now();
    return saveKernelCloudFirst(tenantId, clone);
}

export async function createBlankKernelCloudFirst(tenantId) {
    const existing = listKernels(tenantId);
    const used = new Set(existing.map((k) => k.command));
    let n = 1;
    let command = "new-kernel-1";
    while (used.has(command)) {
        n += 1;
        command = `new-kernel-${n}`;
    }
    const stepId = "s1";
    return saveKernelCloudFirst(tenantId, {
        command,
        title: "Untitled kernel",
        params: {},
        steps: [
            { kind: "python", id: stepId, code: BLANK_KERNEL_STEP_CODE, inputs: {} },
        ],
        render: { kind: "stat", label: "Untitled", format: "int" },
        renderSource: stepId,
    });
}

export function sanitizeCommand(raw) {
    if (!raw) return "";
    const trimmed = String(raw).trim().replace(/^\//, "");
    const slug = trimmed
        .toLowerCase()
        .replace(/[^a-z0-9-_]+/g, "-")
        .replace(/^-+|-+$/g, "");
    return slug.slice(0, 64);
}

function newKernelId() {
    // Browser crypto where available; fall back to timestamp + random
    // for any platform that doesn't expose randomUUID (older Safari).
    try {
        if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
            return crypto.randomUUID();
        }
    } catch { /* fall through */ }
    return `k_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}
